// Bella Vita frontend — ODOO ADAPTER.
// =============================================================================
// Talks ONLY to the controlled Bella Vita API (bellavita_api addon) at
// `${CONFIG.apiBaseUrl}/api/v1/...` — never Odoo RPC / /web, never with any
// admin/master/db credential (owner rule §9).
//
// Activate by building with VITE_BV_ADAPTER=odoo and VITE_BV_API_BASE set to the
// ERP origin (e.g. https://erp.bellavita-sa.com). No screen/component changes.
//
// Customer identity and account management are Odoo Portal/auth_signup owned.
// Account-management methods are intentionally not implemented in this SPA.
// =============================================================================

import { CONFIG } from '../../config.js';
const BASE = () => (CONFIG.apiBaseUrl || '').replace(/\/$/, '') + '/api/v1';

async function req(path, { method = 'GET', body } = {}) {
  const res = await fetch(BASE() + path, {
    method,
    credentials: 'same-origin',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try { data = await res.json(); } catch { /* non-JSON */ }
  if (!res.ok) {
    const e = new Error(data?.error?.message || `HTTP ${res.status}`);
    e.code = data?.error?.code || 'http_error';
    e.status = res.status;
    throw e;
  }
  return data;
}

function notImplemented(method) {
  return async () => {
    const e = new Error(`OdooAdapter.${method}() is managed by Odoo Portal.`);
    e.code = 'ADAPTER_NOT_IMPLEMENTED';
    throw e;
  };
}

// catalogue is small and immutable per session — fetch once
let _catalogP;
const catalog = () => (_catalogP ||= req('/catalog'));


export const odooAdapter = {
  async getPricing() {
    return req('/config');
  },

  async getCategories() {
    return (await catalog()).categories;
  },
  async getServices() {
    return (await catalog()).services;
  },
  async getService(id) {
    return (await catalog()).services.find((s) => String(s.id) === String(id)) || null;
  },
  async getSpecialists({ serviceIds } = {}) {
    const p = new URLSearchParams();
    if (Array.isArray(serviceIds) && serviceIds.length) {
      p.set('service_ids', [...new Set(serviceIds.map(String))].join(','));
    }
    const suffix = p.toString() ? `?${p.toString()}` : '';
    return (await req(`/specialists${suffix}`)).specialists;
  },
  async getPaymentMethods({ amount, serviceIds, hasDeposit, phone, email } = {}) {
    const p = new URLSearchParams();
    if (Number.isFinite(Number(amount))) p.set('amount', String(amount));
    if (Array.isArray(serviceIds) && serviceIds.length) p.set('service_ids', serviceIds.join(','));
    if (typeof hasDeposit === 'boolean') p.set('has_deposit', hasDeposit ? '1' : '0');
    if (phone) p.set('phone', String(phone));
    if (email) p.set('email', String(email));
    const suffix = p.toString() ? `?${p.toString()}` : '';
    return (await req(`/payment-methods${suffix}`)).methods;
  },
  async startPayment({ bookingRef, phone, method }) {
    return (await req('/payments/start', {
      method: 'POST', body: { bookingRef, phone, method },
    })).payment;
  },
  async getPaymentStatus(reference, { bookingRef, phone }) {
    const qs = new URLSearchParams({ booking_ref: bookingRef, phone });
    return req(`/payments/${encodeURIComponent(reference)}/status?${qs.toString()}`);
  },
  async bindMoyasarPayment(reference, { bookingRef, phone, paymentId }) {
    return (await req(`/payments/${encodeURIComponent(reference)}/moyasar/bind`, {
      method: 'POST', body: { bookingRef, phone, paymentId },
    })).payment;
  },

  async getAvailability({ monthISO, specialistId, serviceIds }) {
    const [y, m] = monthISO.split('-').map(Number);
    const sp = specialistId && specialistId !== 'any' ? `&specialist_id=${encodeURIComponent(specialistId)}` : '';
    const svc = Array.isArray(serviceIds) && serviceIds.length
      ? `&service_ids=${encodeURIComponent(serviceIds.join(','))}` : '';
    const { days } = await req(`/availability?year=${y}&month=${m}${sp}${svc}`);
    return days.map((d) => ({ dateISO: d.date, open: d.open, hasSlots: d.has_slots }));
  },
  async getBookingWindow() {
    return req('/booking-window');
  },
  async getNextAvailable({ serviceIds, specialistId, durationMinutes } = {}) {
    const p = new URLSearchParams();
    if (durationMinutes) p.set('duration', String(durationMinutes));
    if (specialistId && specialistId !== 'any') p.set('specialist_id', String(specialistId));
    if (Array.isArray(serviceIds) && serviceIds.length) p.set('service_ids', serviceIds.join(','));
    const { nextAvailable } = await req(`/next-available?${p.toString()}`);
    return nextAvailable;
  },
  async getSlots({ dateISO, specialistId, serviceIds }) {
    const dur = 30; // server recomputes from the actual services on create
    const sp = specialistId && specialistId !== 'any' ? `&specialist_id=${encodeURIComponent(specialistId)}` : '';
    // pass the picked services so the API can pool availability across only the
    // specialists qualified for them (BV-B2-02 capability filtering).
    const svc = Array.isArray(serviceIds) && serviceIds.length
      ? `&service_ids=${encodeURIComponent(serviceIds.join(','))}`
      : '';
    const { slots } = await req(`/slots?date=${encodeURIComponent(dateISO)}&duration=${dur}${sp}${svc}`);
    return slots;
  },

  async getCustomerAuthRoute({ email, phone }) {
    return req('/customer-auth/route', { method: 'POST', body: { email, phone } });
  },
  async sendPortalInvite({ email, phone }) {
    return req('/customer-auth/invite', { method: 'POST', body: { email, phone } });
  },
  async quoteBookingDiscount({ code, serviceIds }) {
    const p = new URLSearchParams({ promo_code: String(code || '') });
    if (Array.isArray(serviceIds) && serviceIds.length) p.set('service_ids', serviceIds.join(','));
    const { discount } = await req(`/payment-methods?${p.toString()}`);
    return discount;
  },
  async createBooking(reqBody) {
    const { booking } = await req('/bookings', { method: 'POST', body: reqBody });
    return booking;
  },
  // Customer booking management belongs to Odoo Portal. The booking SPA does
  // not keep a parallel customer session or authenticate account operations.
  getBooking: notImplemented('getBooking'),
  rescheduleBooking: notImplemented('rescheduleBooking'),
  cancelBooking: notImplemented('cancelBooking'),
  getMyBookings: notImplemented('getMyBookings'),
  requestOtp: notImplemented('requestOtp'),
  verifyOtp: notImplemented('verifyOtp'),
  login: notImplemented('login'),
  logout: notImplemented('logout'),
  currentSession: notImplemented('currentSession'),
};

export default odooAdapter;
